<template>
  <div class="dashboard-container d-flex">
    <!-- Sidebar -->
    <AdminSidebar :sidebarOpen="sidebarOpen" currentPage="advances" />

    <!-- Main Content -->
    <div
      class="main-content flex-grow-1"
      :class="{ 'sidebar-collapsed': !sidebarOpen }"
    >
      <!-- Top Navbar -->
      <AdminNavbar 
        :title="$t('advances.title')" 
        @toggleSidebar="toggleSidebar" 
        @logout="handleLogout" 
      />

      <!-- Content -->
      <div class="container-fluid p-4">
        <!-- Statistics Cards -->
        <div class="row mb-4">
          <div class="col-md-4 mb-3">
            <div class="card border-0 shadow-sm stat-card stat-warning">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="text-muted mb-1 small">{{ $t('advances.pendingRequests') }}</p>
                    <h3 class="mb-0 fw-bold">{{ pendingRequestsCount }}</h3>
                    <small class="text-muted">{{
                      formatCurrency(pendingRequestsAmount)
                    }}</small>
                  </div>
                  <div class="stat-icon bg-warning">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      class="bi bi-hourglass-split"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M2.5 15a.5.5 0 1 1 0-1h1v-1a4.5 4.5 0 0 1 2.557-4.06c.29-.139.443-.377.443-.59v-.7c0-.213-.154-.451-.443-.59A4.5 4.5 0 0 1 3.5 3V2h-1a.5.5 0 0 1 0-1h11a.5.5 0 0 1 0 1h-1v1a4.5 4.5 0 0 1-2.557 4.06c-.29.139-.443.377-.443.59v.7c0 .213.154.451.443.59A4.5 4.5 0 0 1 12.5 13v1h1a.5.5 0 0 1 0 1h-11zm2-13v1c0 .537.12 1.045.337 1.5h6.326c.216-.455.337-.963.337-1.5V2h-7zm3 6.35c0 .701-.478 1.236-1.011 1.492A3.5 3.5 0 0 0 4.5 13s.866-1.299 3-1.48V8.35zm1 0v3.17c2.134.181 3 1.48 3 1.48a3.5 3.5 0 0 0-1.989-3.158C8.978 9.586 8.5 9.052 8.5 8.351z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-4 mb-3">
            <div class="card border-0 shadow-sm stat-card stat-success">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="text-muted mb-1 small">{{ $t('advances.approvedThisMonth') }}</p>
                    <h3 class="mb-0 fw-bold">{{ approvedThisMonthCount }}</h3>
                    <small class="text-muted">{{
                      formatCurrency(approvedThisMonthAmount)
                    }}</small>
                  </div>
                  <div class="stat-icon bg-success">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      class="bi bi-check-circle-fill"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-4 mb-3">
            <div class="card border-0 shadow-sm stat-card stat-info">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="text-muted mb-1 small">{{ $t('advances.outstandingAdvances') }}</p>
                    <h3 class="mb-0 fw-bold">
                      {{ formatCurrency(outstandingAmount) }}
                    </h3>
                    <small class="text-muted">{{ $t('advances.toBeRecovered') }}</small>
                  </div>
                  <div class="stat-icon bg-info">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      class="bi bi-cash-coin"
                      viewBox="0 0 16 16"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M11 15a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm5-4a5 5 0 1 1-10 0 5 5 0 0 1 10 0z"
                      />
                      <path
                        d="M9.438 11.944c.047.596.518 1.06 1.363 1.116v.44h.375v-.443c.875-.061 1.386-.529 1.386-1.207 0-.618-.39-.936-1.09-1.1l-.296-.07v-1.2c.376.043.614.248.671.532h.658c-.047-.575-.54-1.024-1.329-1.073V8.5h-.375v.45c-.747.073-1.255.522-1.255 1.158 0 .562.378.92 1.007 1.066l.248.061v1.272c-.384-.058-.639-.27-.696-.563h-.668zm1.36-1.354c-.369-.085-.569-.26-.569-.522 0-.294.216-.514.572-.578v1.1h-.003zm.432.746c.449.104.655.272.655.569 0 .339-.257.571-.709.614v-1.195l.054.012z"
                      />
                      <path
                        d="M1 0a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h4.083c.058-.344.145-.678.258-1H3a2 2 0 0 0-2-2V3a2 2 0 0 0 2-2h10a2 2 0 0 0 2 2v3.528c.38.34.717.728 1 1.154V1a1 1 0 0 0-1-1H1z"
                      />
                      <path
                        d="M9.998 5.083 10 5a2 2 0 1 0-3.132 1.65 5.982 5.982 0 0 1 3.13-1.567z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Toolbar -->
        <div class="row mb-4">
          <div class="col-md-4 mb-3 mb-md-0">
            <div class="input-group">
              <span class="input-group-text bg-white">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  class="bi bi-search"
                  viewBox="0 0 16 16"
                >
                  <path
                    d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"
                  />
                </svg>
              </span>
              <input
                type="text"
                class="form-control"
                :placeholder="$t('advances.searchPlaceholder')"
                v-model="searchQuery"
              />
            </div>
          </div>
          <div class="col-md-3 mb-3 mb-md-0">
            <select class="form-select" v-model="statusFilter">
              <option value="pending">{{ $t('advances.pending') }}</option>
              <option value="approved">{{ $t('advances.approved') }}</option>
              <option value="rejected">{{ $t('advances.rejected') }}</option>
              <option value="all">{{ $t('common.all') }}</option>
            </select>
          </div>
          <div class="col-md-5 text-md-end">
            <button class="btn btn-primary" @click="openCreateAdvanceModal">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                class="bi bi-plus-lg"
                viewBox="0 0 16 16"
              >
                <path
                  fill-rule="evenodd"
                  d="M8 2a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5A.5.5 0 0 1 8 2Z"
                />
              </svg>
              {{ $t('advances.createAdvance') }}
            </button>
          </div>
        </div>

        <!-- Advances Table -->
        <div class="card border-0 shadow-sm">
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="table-light">
                  <tr>
                    <th @click="sortBy('employeeName')" class="sortable">
                      {{ $t('timesheets.employee') }}
                      <svg
                        v-if="sortField === 'employeeName'"
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        fill="currentColor"
                        :class="{
                          'bi-arrow-up': sortDirection === 'asc',
                          'bi-arrow-down': sortDirection === 'desc',
                        }"
                        viewBox="0 0 16 16"
                      >
                        <path
                          v-if="sortDirection === 'asc'"
                          fill-rule="evenodd"
                          d="M8 15a.5.5 0 0 0 .5-.5V2.707l3.146 3.147a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L7.5 2.707V14.5a.5.5 0 0 0 .5.5z"
                        />
                        <path
                          v-else
                          fill-rule="evenodd"
                          d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1z"
                        />
                      </svg>
                    </th>
                    <th @click="sortBy('amount')" class="sortable">
                      {{ $t('advances.amount') }}
                      <svg
                        v-if="sortField === 'amount'"
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        fill="currentColor"
                        :class="{
                          'bi-arrow-up': sortDirection === 'asc',
                          'bi-arrow-down': sortDirection === 'desc',
                        }"
                        viewBox="0 0 16 16"
                      >
                        <path
                          v-if="sortDirection === 'asc'"
                          fill-rule="evenodd"
                          d="M8 15a.5.5 0 0 0 .5-.5V2.707l3.146 3.147a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L7.5 2.707V14.5a.5.5 0 0 0 .5.5z"
                        />
                        <path
                          v-else
                          fill-rule="evenodd"
                          d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1z"
                        />
                      </svg>
                    </th>
                    <th>{{ $t('advances.reason') }}</th>
                    <th @click="sortBy('requestDate')" class="sortable">
                      {{ $t('advances.requestDate') }}
                      <svg
                        v-if="sortField === 'requestDate'"
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        fill="currentColor"
                        :class="{
                          'bi-arrow-up': sortDirection === 'asc',
                          'bi-arrow-down': sortDirection === 'desc',
                        }"
                        viewBox="0 0 16 16"
                      >
                        <path
                          v-if="sortDirection === 'asc'"
                          fill-rule="evenodd"
                          d="M8 15a.5.5 0 0 0 .5-.5V2.707l3.146 3.147a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L7.5 2.707V14.5a.5.5 0 0 0 .5.5z"
                        />
                        <path
                          v-else
                          fill-rule="evenodd"
                          d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1z"
                        />
                      </svg>
                    </th>
                    <th>{{ $t('advances.requestStatus') }}</th>
                    <th>{{ $t('advances.recoveryStatus') }}</th>
                    <th class="text-end">{{ $t('common.actions') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="advance in paginatedAdvances" :key="advance.id">
                    <td class="fw-semibold">{{ advance.employeeName }}</td>
                    <td class="fw-bold text-success">
                      {{ formatCurrency(advance.amount) }}
                    </td>
                    <td>{{ advance.reason }}</td>
                    <td>{{ formatDate(advance.requestDate) }}</td>
                    <td>
                      <span
                        class="badge"
                        :class="getStatusBadgeClass(advance.status)"
                      >
                        {{ advance.status }}
                      </span>
                    </td>
                    <td>
                      <span
                        v-if="
                          advance.status === 'approved' &&
                          !advance.adjustedInPayroll
                        "
                        class="badge bg-warning text-dark"
                      >
                        {{ $t('advances.outstanding') }}
                      </span>
                      <span
                        v-else-if="advance.adjustedInPayroll"
                        class="badge bg-info"
                      >
                        {{ $t('advances.recovered') }}
                      </span>
                      <span v-else class="text-muted small">-</span>
                    </td>
                    <td class="text-end">
                      <button
                        v-if="advance.status === 'pending'"
                        class="btn btn-sm btn-success me-2"
                        @click="approveAdvance(advance)"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          fill="currentColor"
                          class="bi bi-check-lg"
                          viewBox="0 0 16 16"
                        >
                          <path
                            d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425a.247.247 0 0 1 .02-.022Z"
                          />
                        </svg>
                        {{ $t('advances.approve') }}
                      </button>
                      <button
                        v-if="advance.status === 'pending'"
                        class="btn btn-sm btn-outline-danger"
                        @click="openRejectModal(advance)"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          fill="currentColor"
                          class="bi bi-x-lg"
                          viewBox="0 0 16 16"
                        >
                          <path
                            d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"
                          />
                        </svg>
                        {{ $t('advances.reject') }}
                      </button>
                      <button
                        v-if="advance.status === 'rejected'"
                        class="btn btn-sm btn-outline-info"
                        @click="viewRejectionReason(advance)"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          fill="currentColor"
                          class="bi bi-info-circle"
                          viewBox="0 0 16 16"
                        >
                          <path
                            d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"
                          />
                          <path
                            d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"
                          />
                        </svg>
                        {{ $t('advances.viewReason') }}
                      </button>
                      <button
                        v-if="
                          advance.status === 'approved' &&
                          advance.adjustedInPayroll
                        "
                        class="btn btn-sm btn-outline-secondary"
                        @click="viewRecoveryDetails(advance)"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          fill="currentColor"
                          class="bi bi-info-circle"
                          viewBox="0 0 16 16"
                        >
                          <path
                            d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"
                          />
                          <path
                            d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"
                          />
                        </svg>
                        {{ $t('advances.viewRecovery') }}
                      </button>
                    </td>
                  </tr>
                  <tr v-if="paginatedAdvances.length === 0">
                    <td colspan="7" class="text-center py-4 text-muted">
                      {{ $t('advances.noRequestsFound') }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div class="row mt-3" v-if="totalPages > 1">
          <div class="col">
            <nav>
              <ul class="pagination justify-content-center mb-0">
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <a class="page-link" href="#" @click.prevent="currentPage--"
                    >{{ $t('common.previous') }}</a
                  >
                </li>
                <li
                  v-for="page in totalPages"
                  :key="page"
                  class="page-item"
                  :class="{ active: currentPage === page }"
                >
                  <a
                    class="page-link"
                    href="#"
                    @click.prevent="currentPage = page"
                    >{{ page }}</a
                  >
                </li>
                <li
                  class="page-item"
                  :class="{ disabled: currentPage === totalPages }"
                >
                  <a class="page-link" href="#" @click.prevent="currentPage++"
                    >{{ $t('common.next') }}</a
                  >
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <!-- Reject Modal -->
    <div
      class="modal fade"
      :class="{ show: showRejectModal, 'd-block': showRejectModal }"
      tabindex="-1"
      v-if="showRejectModal"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('advances.rejectAdvanceRequest') }}</h5>
            <button
              type="button"
              class="btn-close"
              @click="closeRejectModal"
            ></button>
          </div>
          <div class="modal-body">
            <p class="mb-3">
              <strong>{{ $t('timesheets.employee') }}:</strong> {{ selectedAdvance?.employeeName
              }}<br />
              <strong>{{ $t('advances.amount') }}:</strong>
              {{ formatCurrency(selectedAdvance?.amount) }}<br />
              <strong>{{ $t('advances.reason') }}:</strong> {{ selectedAdvance?.reason }}
            </p>
            <div class="mb-3">
              <label class="form-label">{{ $t('advances.rejectionReason') }} *</label>
              <textarea
                class="form-control"
                rows="3"
                v-model="rejectionReason"
                :placeholder="$t('advances.rejectionReasonPlaceholder')"
                required
              ></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeRejectModal"
            >
              {{ $t('common.cancel') }}
            </button>
            <button
              type="button"
              class="btn btn-danger"
              @click="confirmReject"
              :disabled="!rejectionReason.trim()"
            >
              {{ $t('advances.rejectRequest') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      class="modal-backdrop fade"
      :class="{ show: showRejectModal }"
      v-if="showRejectModal"
    ></div>

    <!-- View Rejection Reason Modal -->
    <div
      class="modal fade"
      :class="{ show: showReasonModal, 'd-block': showReasonModal }"
      tabindex="-1"
      v-if="showReasonModal"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('advances.rejectionDetails') }}</h5>
            <button
              type="button"
              class="btn-close"
              @click="closeReasonModal"
            ></button>
          </div>
          <div class="modal-body">
            <p class="mb-2">
              <strong>{{ $t('timesheets.employee') }}:</strong> {{ selectedAdvance?.employeeName }}
            </p>
            <p class="mb-2">
              <strong>{{ $t('advances.amount') }}:</strong>
              {{ formatCurrency(selectedAdvance?.amount) }}
            </p>
            <p class="mb-2">
              <strong>{{ $t('advances.requestReason') }}:</strong> {{ selectedAdvance?.reason }}
            </p>
            <hr />
            <p class="mb-2"><strong>{{ $t('advances.rejectionReason') }}:</strong></p>
            <p class="text-danger">{{ selectedAdvance?.rejectionReason }}</p>
            <p class="text-muted small mb-0">
              {{ $t('advances.rejectedOn') }}: {{ formatDate(selectedAdvance?.rejectedDate) }}
            </p>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeReasonModal"
            >
              {{ $t('common.close') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      class="modal-backdrop fade"
      :class="{ show: showReasonModal }"
      v-if="showReasonModal"
    ></div>

    <!-- View Recovery Details Modal -->
    <div
      class="modal fade"
      :class="{ show: showRecoveryModal, 'd-block': showRecoveryModal }"
      tabindex="-1"
      v-if="showRecoveryModal"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('advances.recoveryDetails') }}</h5>
            <button
              type="button"
              class="btn-close"
              @click="closeRecoveryModal"
            ></button>
          </div>
          <div class="modal-body">
            <p class="mb-2">
              <strong>{{ $t('timesheets.employee') }}:</strong> {{ selectedAdvance?.employeeName }}
            </p>
            <p class="mb-2">
              <strong>{{ $t('advances.advanceAmount') }}:</strong>
              {{ formatCurrency(selectedAdvance?.amount) }}
            </p>
            <p class="mb-2">
              <strong>{{ $t('advances.requestReason') }}:</strong> {{ selectedAdvance?.reason }}
            </p>
            <p class="mb-2">
              <strong>{{ $t('advances.approvedOn') }}:</strong>
              {{ formatDate(selectedAdvance?.approvedDate) }}
            </p>
            <hr />
            <p class="mb-2 text-success">
              <strong>{{ $t('common.status') }}:</strong> {{ $t('advances.recoveredFromPayroll') }}
            </p>
            <p class="mb-0 text-muted small">
              {{ $t('advances.recoveredOn') }}: {{ formatDate(selectedAdvance?.recoveredDate) }}
            </p>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeRecoveryModal"
            >
              {{ $t('common.close') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      class="modal-backdrop fade"
      :class="{ show: showRecoveryModal }"
      v-if="showRecoveryModal"
    ></div>

    <!-- Create Advance Modal -->
    <div
      class="modal fade"
      :class="{ show: showCreateModal, 'd-block': showCreateModal }"
      tabindex="-1"
      v-if="showCreateModal"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('advances.createAdvanceEntry') }}</h5>
            <button
              type="button"
              class="btn-close"
              @click="closeCreateModal"
            ></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">{{ $t('timesheets.employee') }} *</label>
              <select
                class="form-select"
                v-model="newAdvance.employeeId"
                required
              >
                <option value="">{{ $t('advances.selectEmployee') }}</option>
                <option
                  v-for="employee in employees"
                  :key="employee.id"
                  :value="employee.id"
                >
                  {{ employee.name }} - {{ employee.position }}
                </option>
              </select>
            </div>
            <div class="mb-3">
              <label class="form-label">{{ $t('advances.amount') }} (€) *</label>
              <input
                type="number"
                class="form-control"
                v-model.number="newAdvance.amount"
                :placeholder="$t('advances.enterAmount')"
                min="0"
                step="0.01"
                required
              />
            </div>
            <div class="mb-3">
              <label class="form-label">{{ $t('advances.reason') }} *</label>
              <textarea
                class="form-control"
                rows="3"
                v-model="newAdvance.reason"
                :placeholder="$t('advances.enterReasonForAdvance')"
                required
              ></textarea>
            </div>
            <div class="alert alert-info mb-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                class="bi bi-info-circle me-2"
                viewBox="0 0 16 16"
              >
                <path
                  d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"
                />
                <path
                  d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"
                />
              </svg>
              {{ $t('advances.autoApprovalInfo') }}
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeCreateModal"
            >
              {{ $t('common.cancel') }}
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="createAdvance"
              :disabled="!isCreateFormValid || loading"
            >
              <span
                v-if="loading"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              {{ $t('advances.createAdvance') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      class="modal-backdrop fade"
      :class="{ show: showCreateModal }"
      v-if="showCreateModal"
    ></div>
  </div>
</template>

<script>
import axios from "axios";
import AdminSidebar from "@/components/AdminSidebar.vue";
import AdminNavbar from "@/components/AdminNavbar.vue";

const API_URL = process.env.VUE_APP_API_URL || "http://localhost:4000";

export default {
  name: "Advances",
  components: {
    AdminSidebar,
    AdminNavbar,
  },
  data() {
    return {
      sidebarOpen: true,
      loading: false,
      searchQuery: "",
      statusFilter: "approved",
      sortField: "requestDate",
      sortDirection: "desc",
      currentPage: 1,
      itemsPerPage: 10,
      showRejectModal: false,
      showReasonModal: false,
      showRecoveryModal: false,
      showCreateModal: false,
      selectedAdvance: null,
      rejectionReason: "",
      approvalComments: "",
      advances: [],
      employees: [],
      statistics: {},
      loading: false,
      newAdvance: {
        employeeId: "",
        amount: "",
        reason: "",
      },
    };
  },
  async mounted() {
    const userData = localStorage.getItem("user");
    if (!userData) {
      this.$router.push("/");
      return;
    }
    const user = JSON.parse(userData);
    if (user.role !== "admin") {
      this.$router.push("/employee/dashboard");
      return;
    }

    await this.loadStatistics();
    await this.loadAdvances();
    await this.loadEmployees();
  },
  computed: {
    pendingRequestsCount() {
      return this.statistics.pending_count || 0;
    },
    pendingRequestsAmount() {
      return parseFloat(this.statistics.pending_amount || 0);
    },
    approvedThisMonthCount() {
      return this.statistics.approved_count || 0;
    },
    approvedThisMonthAmount() {
      return parseFloat(this.statistics.approved_amount || 0);
    },
    outstandingAmount() {
      return parseFloat(this.statistics.outstanding_amount || 0);
    },
    filteredAdvances() {
      let filtered = this.advances;

      // Search filter (backend already filtered by status)
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter((a) =>
          (a.employee_name || a.employeeName || "")
            .toLowerCase()
            .includes(query)
        );
      }

      // Sorting
      filtered.sort((a, b) => {
        let aVal = a[this.sortField] || a.requestDate || a.request_date;
        let bVal = b[this.sortField] || b.requestDate || b.request_date;

        if (this.sortField === "requestDate") {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
        }

        if (aVal < bVal) return this.sortDirection === "asc" ? -1 : 1;
        if (aVal > bVal) return this.sortDirection === "asc" ? 1 : -1;
        return 0;
      });

      return filtered;
    },
    paginatedAdvances() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredAdvances.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredAdvances.length / this.itemsPerPage);
    },
    isCreateFormValid() {
      return (
        this.newAdvance.employeeId &&
        this.newAdvance.amount > 0 &&
        this.newAdvance.reason.trim()
      );
    },
  },
  watch: {
    statusFilter() {
      this.currentPage = 1; // Reset to first page when filter changes
      this.loadAdvances();
    },
  },
  methods: {
    getAuthHeaders() {
      const userData = localStorage.getItem("user");
      if (userData) {
        const user = JSON.parse(userData);
        return {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "application/json",
        };
      }
      return {};
    },
    async loadStatistics() {
      try {
        const response = await axios.get(
          `${API_URL}/api/advances/admin/statistics`,
          { headers: this.getAuthHeaders() }
        );
        if (response.data.success) {
          this.statistics = response.data.statistics;
        }
      } catch (error) {
        console.error("Load statistics error:", error);
        this.handleAuthError(error);
      }
    },
    async loadAdvances() {
      try {
        this.loading = true;
        const params =
          this.statusFilter !== "all" ? `?status=${this.statusFilter}` : "";
        const response = await axios.get(
          `${API_URL}/api/advances/admin/all${params}`,
          { headers: this.getAuthHeaders() }
        );
        if (response.data.success) {
          this.advances = response.data.advances;
        }
      } catch (error) {
        console.error("Load advances error:", error);
        this.handleAuthError(error);
      } finally {
        this.loading = false;
      }
    },
    async loadEmployees() {
      try {
        const response = await axios.get(`${API_URL}/api/employees`, {
          headers: this.getAuthHeaders(),
        });
        if (response.data.success) {
          this.employees = response.data.employees;
        }
      } catch (error) {
        console.error("Load employees error:", error);
        this.handleAuthError(error);
      }
    },
    async approveAdvance(advance) {
      if (
        !confirm(
          `Approve advance of ${this.formatCurrency(advance.amount)} for ${
            advance.employee_name
          }?`
        )
      ) {
        return;
      }

      try {
        this.loading = true;
        const response = await axios.put(
          `${API_URL}/api/advances/${advance.id}/approve`,
          { comments: this.approvalComments },
          { headers: this.getAuthHeaders() }
        );

        if (response.data.success) {
          alert("Advance approved successfully!");
          await this.loadStatistics();
          await this.loadAdvances();
        }
      } catch (error) {
        console.error("Approve advance error:", error);
        this.handleAuthError(error);
        alert("Failed to approve advance. Please try again.");
      } finally {
        this.loading = false;
      }
    },
    async confirmReject() {
      if (!this.rejectionReason.trim()) {
        alert("Please provide a rejection reason");
        return;
      }

      try {
        this.loading = true;
        const response = await axios.put(
          `${API_URL}/api/advances/${this.selectedAdvance.id}/reject`,
          { reason: this.rejectionReason },
          { headers: this.getAuthHeaders() }
        );

        if (response.data.success) {
          alert("Advance rejected successfully!");
          this.closeRejectModal();
          await this.loadStatistics();
          await this.loadAdvances();
        }
      } catch (error) {
        console.error("Reject advance error:", error);
        this.handleAuthError(error);
        alert("Failed to reject advance. Please try again.");
      } finally {
        this.loading = false;
      }
    },
    handleAuthError(error) {
      if (error.response && error.response.status === 401) {
        alert("Session expired. Please login again.");
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        this.$router.push("/");
      }
    },
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen;
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
      }).format(amount);
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString("en-GB");
    },
    getStatusBadgeClass(status) {
      const classes = {
        pending: "bg-warning",
        approved: "bg-success",
        rejected: "bg-danger",
      };
      return classes[status] || "bg-secondary";
    },
    sortBy(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortField = field;
        this.sortDirection = "asc";
      }
    },
    openRejectModal(advance) {
      this.selectedAdvance = advance;
      this.rejectionReason = "";
      this.showRejectModal = true;
    },
    closeRejectModal() {
      this.showRejectModal = false;
      this.selectedAdvance = null;
      this.rejectionReason = "";
    },
    viewRejectionReason(advance) {
      this.selectedAdvance = advance;
      this.showReasonModal = true;
    },
    closeReasonModal() {
      this.showReasonModal = false;
      this.selectedAdvance = null;
    },
    viewRecoveryDetails(advance) {
      this.selectedAdvance = advance;
      this.showRecoveryModal = true;
    },
    closeRecoveryModal() {
      this.showRecoveryModal = false;
      this.selectedAdvance = null;
    },
    openCreateAdvanceModal() {
      this.showCreateModal = true;
      this.newAdvance = {
        employeeId: "",
        amount: "",
        reason: "",
      };
    },
    closeCreateModal() {
      this.showCreateModal = false;
      this.newAdvance = {
        employeeId: "",
        amount: "",
        reason: "",
      };
    },
    async createAdvance() {
      if (!this.isCreateFormValid) {
        alert("Please fill in all required fields");
        return;
      }

      if (
        !confirm(
          `Create advance of ${this.formatCurrency(
            this.newAdvance.amount
          )} for this employee?`
        )
      ) {
        return;
      }

      try {
        this.loading = true;
        const response = await axios.post(
          `${API_URL}/api/advances/admin/create`,
          this.newAdvance,
          { headers: this.getAuthHeaders() }
        );

        if (response.data.success) {
          alert("Advance created successfully!");
          this.closeCreateModal();
          await this.loadStatistics();
          await this.loadAdvances();
        }
      } catch (error) {
        console.error("Create advance error:", error);
        this.handleAuthError(error);
        alert(
          error.response?.data?.message ||
            "Failed to create advance. Please try again."
        );
      } finally {
        this.loading = false;
      }
    },
    handleLogout() {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      this.$router.push("/");
    },
  },
};
</script>

<style scoped>
.dashboard-container {
  min-height: 100vh;
  background-color: #f8f9fa;
}

/* Sidebar Styles */
.sidebar {
  width: 280px;
  background-color: #2c3e50;
  color: white;
  min-height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  overflow-y: auto;
  transition: all 0.3s ease;
  z-index: 1000;
}

.sidebar.collapsed {
  width: 70px;
}

.sidebar-header {
  padding: 2rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: padding 0.3s ease;
}

.sidebar.collapsed .sidebar-header {
  padding: 2rem 0.5rem;
}

.sidebar-header h4 {
  color: white;
  font-weight: 600;
  font-size: 1.5rem;
  white-space: nowrap;
  overflow: hidden;
}

.sidebar-nav {
  padding: 1rem 0;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1.05rem;
  gap: 1rem;
  white-space: nowrap;
  overflow: hidden;
}

.sidebar.collapsed .nav-item {
  padding: 1rem 0.5rem;
  justify-content: center;
  font-size: 0;
}

.sidebar.collapsed .nav-item svg {
  margin: 0;
}

.nav-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
}

.nav-item.active {
  background-color: rgba(255, 255, 255, 0.15);
  color: white;
  border-left: 4px solid #667eea;
}

.nav-item svg {
  flex-shrink: 0;
}

/* Main Content */
.main-content {
  margin-left: 280px;
  min-height: 100vh;
  background-color: #f8f9fa;
  transition: margin-left 0.3s ease;
}

.main-content.sidebar-collapsed {
  margin-left: 70px;
}

.navbar {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Statistics Cards */
.stat-card {
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

/* Table */
.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.85rem;
  letter-spacing: 0.5px;
  color: #6c757d;
}

.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  background-color: #e9ecef !important;
}

.modal.show {
  background-color: rgba(0, 0, 0, 0.5);
}

/* Responsive */
@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
  }

  .sidebar:not(.collapsed) {
    transform: translateX(0);
  }

  .main-content {
    margin-left: 0 !important;
  }
}
</style>
